import TableCell from "@mui/material/TableCell"
import { Link as RouterLink } from "react-router-dom"

import Column from "../../../common/components/Column"
import { trimLongString } from "../../../common/utils"
import { adminApi } from "../axios"
import AdminConciergeMagicLoginActions from "../components/AdminConciergeMagicLoginActions"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListToolbar from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"

const YES_NO = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
]

export const CONCIERGE_FILTER_SPECS = {
  active: {
    default: "true",
    toParams: (value) =>
      value === "true" || value === "false" ? { active: value } : {},
  },
  assignments: { param: "assignments" },
  number: { param: "has_number" },
  mailbox: { param: "has_mailbox" },
}

const TOOLBAR_FILTERS = [
  {
    key: "active",
    label: "Active only",
    type: "switch",
    onValue: "true",
    offValue: "all",
    chip: true,
    chipLabel: "Including inactive",
  },
  {
    key: "assignments",
    label: "Assignments",
    type: "select",
    options: [
      { value: "none", label: "None" },
      { value: "some", label: "1 or more" },
      { value: "max", label: "At the limit" },
    ],
  },
  { key: "number", label: "Has number", type: "select", options: YES_NO },
  { key: "mailbox", label: "Has mailbox", type: "select", options: YES_NO },
]

export default function AdminConcierges() {
  const query = useAdminPaginatedQuery(adminApi.fetchConcierges, {
    filters: CONCIERGE_FILTER_SPECS,
    defaultSortColumn: "name",
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
  } = query

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Concierge"
        subtitle="Concierge operators — open a row for assignments and activity."
      />
      <AdminListToolbar
        query={query}
        searchLabel="Search concierge"
        noun={totalResultsSize === 1 ? "concierge" : "concierges"}
        filters={TOOLBAR_FILTERS}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={5}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="name"
              label="Name"
              query={query}
              minWidth={{ xs: 72, sm: 96 }}
            />
            <AdminSortableHeaderCell
              columnName="email"
              label="Email"
              query={query}
              minWidth={{ xs: 88, sm: 120 }}
            />
            <AdminSortableHeaderCell
              columnName="active_assignment_count"
              label="Active assignments"
              query={query}
              align="right"
              minWidth={{ xs: 72, sm: 120 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
              Status
            </AdminHeaderTableCell>
            <AdminHeaderTableCell align="right" minWidth={{ xs: 88, sm: 120 }}>
              Login
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((row) => (
          <AdminTableRow key={row.id} hover>
            <TableCell
              sx={{
                ...adminTableBodyCellSx,
                maxWidth: { xs: 160, sm: 200, md: 220 },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={row.name || ""}
            >
              <RouterLink to={`/admin/concierges/${row.id}`}>
                {trimLongString(row.name || "—", 20)}
              </RouterLink>
            </TableCell>
            <AdminTruncatedCell value={row.email} />
            <AdminTruncatedCell
              value={row.active_assignment_count ?? 0}
              align="right"
            />
            <AdminTruncatedCell value={row.is_active ? "Active" : "Inactive"} />
            <TableCell
              align="right"
              sx={{
                ...adminTableBodyCellSx,
                textAlign: "right",
              }}
            >
              <AdminConciergeMagicLoginActions
                loginLink={row.login_link}
                compact
              />
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>
    </Column>
  )
}
